import React, {useEffect, useState} from 'react';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import ListItemIcon from '@mui/material/ListItemIcon';
import DescriptionIcon from '@mui/icons-material/Description';
import {Tooltip} from "@mui/material";
import Actions from "./Actions";
import PublicOffIcon from '@mui/icons-material/PublicOff';
import PublicIcon from '@mui/icons-material/Public';
import {useStores} from "../../../Hooks";
import InfoIcon from "@mui/icons-material/Info";
import {projectStore} from "../../../index";

const CustomListItem = (
    {
        result,
        searchInput,
        getIcon,
        getText,
        editPromissions,
        handleClose,
        highlightText,
        isSearching,
        setIsSearching,
    }) => {
    const {generalStore} = useStores();
    const [isHovered, setIsHovered] = useState(false);
    const [isHoveredSearch, setIsHoveredSearch] = useState(false);
    const [fullpath, setFullpath] = useState('');

    const truncateText = (text, maxLength) => {
        if (text.length > maxLength) {
            return `${text.substring(0, maxLength)}...`;
        }
        return text;
    };

    const displayText = isHovered
        ? (result.type === 'procedures' ? highlightText(truncateText(result.name, 18), searchInput) : highlightText(truncateText(result.name, 30), searchInput))
        : highlightText(truncateText(result.name, 35), searchInput);

    const asyncSearch = async () => {
        setIsSearching(true);
        const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 2000));

        const searchPromise = async () => {
            const answer = await generalStore.globalSearchGetFullPath(result.ParentFolder);
            const {ProjectName} = await projectStore.getProjectById(result.ProjectRef);
            switch (result.type) {
                case 'procedures':
                    setFullpath(`${ProjectName}/${answer}/${result.ProcedureName}`)
                    return;
                case 'folders':
                    setFullpath(`${ProjectName}/${answer}/${result.FolderName}`)
                    return;
                default:
                    setFullpath(`${ProjectName}`)
                    return;
            }
        };

        Promise.race([searchPromise(), timeoutPromise]).then(() => {
            setIsSearching(false);
        }).catch(error => {
            console.error("An error occurred during the search operation:", error);
            setIsSearching(false);
        });
    }

    const asyncSearchTopFolder = async () => {
        setIsSearching(true);
        const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 2000));

        const searchPromise = async () => {
            const {ProjectName} = await projectStore.getProjectById(result?.ProjectRef);
            switch (result.type) {
                case 'procedures':
                    setFullpath(`${ProjectName}/${result.ProcedureName}`)
                    return;
                case 'folders':
                    setFullpath(`${ProjectName}/${result.FolderName}`)
                    return;
                default:
                    setFullpath(`${ProjectName}`)
                    return;
            }
        };

        Promise.race([searchPromise(), timeoutPromise]).then(() => {
            setIsSearching(false);
        }).catch(error => {
            console.error("An error occurred during the search operation:", error);
            setIsSearching(false);
        });
    }



    useEffect(() => {
        if (!isHovered || !isHoveredSearch || fullpath.length > 0 || isSearching) return;
        if (result.type === 'projects') return setFullpath(`${result.ProjectName}`);
        if (result?.ParentFolder === 'root' || result.mirrored) {
            asyncSearchTopFolder();
            return;
        }
        if (isSearching) {
            return;
        }

        if (isHoveredSearch) {
            asyncSearch();
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHoveredSearch]);


    return (
        <Tooltip
            title={result.name}
            arrow
            classes="search-global-result"
            disableInteractive
        >
        <ListItem
            className="search-global-result-listItem"
            onMouseEnter={() => {
                setIsHovered(true)
            }}
            onMouseLeave={
            () => {
                setIsHovered(false)
                setIsHoveredSearch(false)
            }}
        >
            <ListItemIcon className="search-global-result-icon">
                {getIcon(result) || <DescriptionIcon />}
            </ListItemIcon>
            <ListItemText
                primary={
                    <React.Fragment>
                        <div className="search-global-result-icon-frame">
                            {result.type === 'procedures' ? (
                                <div className="search-global-result-icon-frame">
                                    {!result?.IsPublished ? (
                                        <PublicOffIcon className="search-global-result-icon-unpublished"/>
                                    ) : (
                                        <PublicIcon className="search-global-result-icon-public"/>
                                    )}
                                </div>
                            ) : null}
                            {displayText}
                            {result.mirrored ? (
                                <Tooltip
                                    title="This item is referenced from a different workspace"
                                    arrow
                                >
                                    <InfoIcon
                                        style={{marginLeft: '5px'}}
                                        className='card-info-icon'
                                        htmlColor='#36415D'
                                    />
                                </Tooltip>
                            ) : null}
                        </div>
                    </React.Fragment>
                }
                secondary={getText(result)}
                className="search-global-result-listItemText"
            />
            <div className="search-global-result-actions">
                <Actions
                    editPromissions={editPromissions}
                    result={result}
                    handleClose={handleClose}
                    fullpath={fullpath}
                    isHoveredSearch={isHoveredSearch}
                    setIsHoveredSearch={setIsHoveredSearch}
                />
            </div>
        </ListItem>
        </Tooltip>
    );
};

export default CustomListItem;
